import React, { useEffect, useState } from 'react'
import { Button, Dropdown, Form, Grid, Icon, Image, List, Segment } from 'semantic-ui-react'
import locked from '../../../../assets/svg/locked.svg'
import select_icon from '../../../../assets/svg/modal_select_icon.svg'
import unselect_icon from '../../../../assets/svg/modal_unselected_icon.svg'
import unlocked from '../../../../assets/svg/unlocked.svg'
import CircularLabel from '../../../../components/Labels/CircularLabel'
import WizardComponent from '../../../../components/Modal/WizardComponent'
import { IUserDetail } from '../../../../types/user'
import { checkRole } from '../../../../utils/checkRole'
import { createInitials } from '../../../../utils/createInitials'
import { IEmployee } from '../../../settings/types/employee'
import { ProjectPermissions } from '../../types/project'
import { IWizardProject } from '../../types/wizardData'

interface Props {
  employees: IEmployee[]
  open: boolean
  data: IWizardProject
  wizardFinishedSteps: number[]
  user: IUserDetail | null
  setWizardFinishedSteps: (steps: number[]) => void
  setWizardStep: (step: string) => void
  setProjectWizardData: (data: IWizardProject) => void
  onClose: () => void
}

const permissions = [
  { id: 1, type: ['restricted', 'Restricted Project'] as ProjectPermissions, rights: [] },
  { id: 2, type: ['open', 'Open Project'] as ProjectPermissions, rights: [10] }
]

const projectTypes = [
  { id: 1, rights: [7], type: 'sponsor' },
  { id: 2, rights: [9], type: 'service_provider' },
  { id: 3, rights: [8], type: 'internal' }
]

const WizardSelectTeam = (props: Props): JSX.Element => {
  const {
    user,
    employees,
    data,
    setWizardStep,
    setProjectWizardData,
    setWizardFinishedSteps,
    wizardFinishedSteps,
    open,
    onClose
  } = props

  const [selectedItem, setSelectedItem] = useState<string | number>(data.employees.length > 0 ? data.employees[0].id : '')
  const [employeeList, setEmployeeList] = useState<IEmployee[]>([...data.employees])
  const [selectedPermissions, setSelectedPermissions] = useState<ProjectPermissions[0]>(data.permissions)
  const [permissionsList, setPermissionsList] = useState(permissions)

  useEffect(() => {
    if (wizardFinishedSteps.includes(5)) {
      if (selectedPermissions.length === 0) {
        setWizardFinishedSteps([1, 2, 3, 4])
      }
    }
  }, [setWizardFinishedSteps, wizardFinishedSteps, selectedPermissions])

  useEffect(() => {
    const _permissions = permissions.filter(p => {
      if (user && checkRole(user, projectTypes.filter(r => r.type === data.type)[0].rights) && p.type[0] === 'restricted') {
        return p
      }
      if (user && checkRole(user, p.rights) && p.type[0] === 'open') {
        return p
      }
    })

    if (user && !checkRole(user, [10])) {
      setSelectedPermissions('restricted')
    } else if (_permissions.length === 1) {
      setSelectedPermissions('open')
    }
    setPermissionsList(_permissions)
  }, [user, data.type])

  const handleSelectEmployee = (employee: IEmployee) => {
    if (employeeList.find(empl => empl.id === employee.id) === undefined) {
      setEmployeeList([...employeeList, employee])
      setSelectedItem(employee.id)
    }
  }


  // remove empolyee from list and set selected value to empty string
  const removeEmployee = (item: IEmployee) => {
    const list = employeeList.filter(i => i.id !== item.id)
    setEmployeeList(list)
    setSelectedItem('')
  }

  const goToNextStep = () => {
    const _data: IWizardProject = {
      ...data,
      employees: employeeList,
      permissions: selectedPermissions
    }
    if (!wizardFinishedSteps.includes(5)) {
      setWizardFinishedSteps([1, 2, 3, 4, 5])
    }
    setWizardStep('step-five')
    setProjectWizardData(_data)
  }

  const goToPreviousStep = () => {
    const _data: IWizardProject = {
      ...data,
      employees: employeeList,
      permissions: selectedPermissions
    }
    setWizardStep('step-three')
    setProjectWizardData(_data)
  }

  const renderEmployeeList = () => {
    if (employeeList.filter(i => i.id !== '').length !== 0 && selectedPermissions !== 'open' && selectedPermissions === 'restricted') {
      return (
        <List style={{ overflowY: 'auto', scrollbarWidth: 'thin', maxHeight: '13rem', paddingBottom: '2rem', position: 'relative', zIndex: 99 }}>
          {employeeList.map((item, idx) => {
            if (item.id !== '') {
              const content = item.photo ?
                <Image
                  circular={true}
                  src={item.photo}
                  style={{ width: '1.8rem', height: '1.8rem', minWidth: '1.8rem', minHeight: '1.8rem', objectFit: 'cover' }} />
                : createInitials(item)
              return (
                <List.Item style={{ lineHeight: '3rem', borderTop: '1px solid #F3F3F3' }} key={idx}>
                  <List.Content style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '.5rem', margin: 'auto' }}>
                    <div style={{ display: 'flex', marginBottom: 0, alignItems: 'baseline' }}>
                      <CircularLabel style={{ marginRight: '1.5rem', marginLeft: '1rem' }} key={`${item.id}-emp`} height='2rem' width='2rem' content={content} color={item.photo ? '' : item.color} />
                      <p style={{ marginLeft: '.5rem', fontWeight: 'bold', width: '15rem' }}>{item.full_name}</p>
                      <p style={{ fontSize: '12px', opacity: .5 }}>{item.department_name}</p>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <List.Icon onClick={() => removeEmployee(item)} color='red' style={{ fontSize: '18px', cursor: 'pointer' }} name='times circle' />
                    </div>
                  </List.Content>
                </List.Item>
              )
            }
            return null
          })}
        </List>
      )
    }
    return null
  }

  const onClick = (type: string) => {
    setSelectedPermissions(type)
  }

  const renderPermissions = () => {
    return (
      <Grid.Row columns={2} style={{ paddingTop: '.4rem', paddingBottom: '24px' }}>
        {permissionsList.map(p => {
          return (
            <Grid.Column key={p.id} style={{ paddingRight: p.id === 1 ? '31px' : '' }}>
              <Segment
                onClick={() => onClick(p.type[0])}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  opacity: p.type[0] === selectedPermissions ? 1 : .6,
                  border: `2px solid ${p.type[0] === selectedPermissions ? '#08D6A1' : '#BEC8C3'}`,
                  backgroundColor: 'transparent',
                  padding: '18px 22px',
                  textAlign: 'left',
                  color: '#232C58',
                  height: '56px'
                }}>
                <div style={{ display: 'flex', justifyContent: 'left', fontWeight: 'bold' }}>
                  <Image src={p.type[0] === 'restricted' ? locked : unlocked} style={{ marginRight: '15px', height: '1.3rem' }} />
                  {p.type[1]}
                </div>
                <Image style={{ float: 'right', height: '1.3rem', marginTop: '-1.3rem' }} src={p.type[0] === selectedPermissions ? select_icon : unselect_icon} />
              </Segment>
            </Grid.Column>
          )
        })}
      </Grid.Row>
    )
  }

  const renderCheckbox = (checked: boolean) => {
    if (checked) {
      return (
        <Button
          style={{
            height: '20px',
            width: '20px',
            borderRadius: '6px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginLeft: '8px',
            boxShadow: '0 0 25px -5px rgba(21,21,21,0.1)'
          }}
          primary={true}
          icon='check'
        />
      )
    }
    return (
      <div
        style={{
          cursor: 'pointer',
          marginLeft: '8px',
          marginRight: '0.3rem',
          background: '#FFFFFF',
          height: '21px',
          width: '21px',
          borderRadius: '6px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0 0 25px -5px rgba(21,21,21,0.1)',
          border: '1px solid #E5EBF0'
        }}
      />
    )
  }

  const renderDropdown = () => {
    const _options = employees.map(emp => {
      const checked = employeeList.find(empl => empl.id === emp.id) !== undefined
      const content = emp.photo ?
        <Image
          circular={true}
          src={emp.photo}
          style={{ width: `1.8rem`, height: `1.8rem`, minWidth: `1.8rem`, minHeight: `1.8rem`, objectFit: 'cover' }} />
        : createInitials(emp)
      return {
        key: emp.id,
        text: emp.full_name,
        value: emp.id,
        content: (
          <div style={{ display: 'flex', alignItems: 'center', height: '16px' }} onClick={() => handleSelectEmployee(emp)}>
            {renderCheckbox(checked)}
            <CircularLabel style={{ marginRight: '1.5rem', marginLeft: '1rem' }} key={`${emp.id}-emp`} height='2rem' width='2rem' content={content} color={emp.photo ? '' : emp.color} />
            <div style={{ width: '50%' }}>{emp.full_name}</div>
            <div style={{ width: '30%', textAlign: 'right' }}>{emp.department_name}</div>
          </div>
        ),
        disabled: checked
      }
    })

    return (
      <Form.Field>
        <label style={{ display: 'flex', alignItems: 'center' }}>
          <p style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, letterSpacing: '.25px', color: '#172533' }}>Member</p>
        </label>
        <Dropdown
          search={true}
          selection={true}
          fluid={true}
          closeOnChange={false}
          floating={true}
          options={_options}
          value={selectedItem}
          placeholder='Select Employee'
          header={<div style={{ color: '#747678', fontSize: '12px', padding: '16px 24px 8px' }}>Employees</div>}
          icon={<Icon name='caret down' style={{ float: 'right', fontSize: '1rem', marginRight: '-8px', color: '#08D6A1', marginTop: '8px' }} />}
          className='select-team-dropdown'
        />
      </Form.Field>
    )
  }



  const renderContent = () => {
    return (
      <React.Fragment>
        <Grid>
          <label style={{ display: 'flex', alignItems: 'center' }}>
            <p style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, letterSpacing: '.25px', color: '#172533' }}>Permissions</p>
          </label>
          {renderPermissions()}
        </Grid>
        {selectedPermissions === 'restricted' ?
          <Form style={{ marginTop: '1.5rem' }}>
            {renderDropdown()}
          </Form> : null}
        {renderEmployeeList()}
      </React.Fragment>
    )
  }

  const renderFooter = () => {
    return (
      <div>
        <Button
          inverted
          color='grey'
          size='small'
          onClick={goToPreviousStep}
          content='Back'
          style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0, marginRight: '1rem', color: '#747678', borderColor: '#BEC8C3' }}
        />
        <Button
          disabled={selectedPermissions.length === 0}
          primary={true}
          size='small'
          onClick={goToNextStep}
          content='Next'
          style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0, margin: 0 }}
        />
      </div>
    )
  }

  const renderWizard = () => {
    const _data: IWizardProject = {
      ...data,
      employees: employeeList,
      permissions: selectedPermissions
    }
    const _steps = [
      { key: 1, name: 'step-one' },
      { key: 2, name: 'step-two' },
      { key: 3, name: 'step-three' },
      { key: 4, name: 'step-four' },
      { key: 5, name: 'step-five' },
      { key: 6, name: 'step-six' }
    ]
    return (
      <WizardComponent
        open={open}
        close={onClose}
        content={renderContent()}
        headerContent='Select Project Team'
        headerColor='#232C58'
        footer={renderFooter()}
        steps={data.type === 'service_provider' ? 6 : 5}
        activeStep={4}
        width={'756px'}
        finishedSteps={wizardFinishedSteps}
        setWizardStep={setWizardStep}
        setProjectWizardData={setProjectWizardData}
        data={_data}
        stepStringList={_steps}
        headerContentFontSize='28px'
      />
    )
  }

  return renderWizard()
}

export default WizardSelectTeam
